import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { useHotkeys } from 'react-hotkeys-hook';

import { apiDelete } from '../../lib/api_v2';
import { buildMessageEntitySnapshots } from '../../lib/entitySnapshots';
import { logError } from '../../lib/logger';
import { syncWorkspaceLatestMessageChanges } from '../../lib/workspaceLatestMessages';
import { useMessageAuthor } from '../../hooks/useMessageAuthor';
import { useResolvedMessage } from '../../hooks/useResolvedMessage';
import { useRetainedFeedMessage } from '../../hooks/useRetainedFeedMessage';

import { EntitySnapshotContext } from '../../contexts/EntitySnapshotContext';
import Message from '../messages/Message';
import SettingsButton from '../shared/SettingsButton';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalHeader } from './ModalHeader';
import { useModalContext } from './ModalPortal';
import { ModalTitle } from './ModalTitle';

// `message` is the renderer row retained when the confirmation opened, so feed-only
// messages preview and delete without a duplicate row in the legacy messages table.
const DeleteMessageConfirmation = ({ messageId, message: retainedMessage = null, onClose, onDeleted = null }) => {
  const { unlocked } = useModalContext();
  const entitySnapshots = useContext(EntitySnapshotContext);

  const { message, isLoading, deleted } = useRetainedFeedMessage(messageId, retainedMessage);

  // A deletion signal during the close fade after our own confirmed delete must not
  // flash the "already deleted" terminal state; only someone else's delete should.
  const selfDeletedRef = useRef(false);
  useEffect(() => {
    selfDeletedRef.current = false;
  }, [messageId]);
  const deletedByOther = deleted && !selfDeletedRef.current;

  const workspaceId = message?.workspace_id;
  const author = useMessageAuthor(message, workspaceId, retainedMessage?.author);

  // The retained renderer row usually carries its already-resolved reply parent (raw feed
  // rows never do); otherwise resolve it from the durable feed cache with an API fallback
  // for off-window parents. allowLegacy keeps offline previews working for parents that
  // only ever arrived through legacy paths; online, the network step still wins over a
  // stale legacy row.
  const inlineReplyParentCandidate = retainedMessage?.replyToMessage ?? message?.replyToMessage;
  const inlineReplyParent =
    inlineReplyParentCandidate && typeof inlineReplyParentCandidate === 'object' ? inlineReplyParentCandidate : null;
  const { message: resolvedReplyParent, isLoading: replyParentLoading } = useResolvedMessage(message?.reply_to_message_id, {
    channelId: message?.channel_id,
    allowNetwork: true,
    allowLegacy: true,
    inlineMessage: inlineReplyParent,
  });
  const replyToAuthor = useMessageAuthor(resolvedReplyParent, workspaceId);

  // build fresh entity snapshots for the message to ensure entities show correctly
  const messageEntitySnapshots = useMemo(() => {
    if (!message?.text || !entitySnapshots) return null;
    return buildMessageEntitySnapshots(message.text, entitySnapshots);
  }, [message?.text, entitySnapshots]);

  const replyEntitySnapshots = useMemo(() => {
    if (!resolvedReplyParent?.text || !entitySnapshots) return null;
    return buildMessageEntitySnapshots(resolvedReplyParent.text, entitySnapshots);
  }, [resolvedReplyParent, entitySnapshots]);

  // create message object with fresh entity snapshots
  const messageWithEntitySnapshots = useMemo(() => {
    if (!message) return null;
    if (!messageEntitySnapshots) return message;
    return { ...message, entity_snapshots: messageEntitySnapshots };
  }, [message, messageEntitySnapshots]);

  const replyToMessageWithEntitySnapshots = useMemo(() => {
    if (!resolvedReplyParent) return null;
    if (!replyEntitySnapshots) return { ...resolvedReplyParent, author: replyToAuthor };
    return {
      ...resolvedReplyParent,
      author: replyToAuthor,
      entity_snapshots: replyEntitySnapshots,
    };
  }, [resolvedReplyParent, replyToAuthor, replyEntitySnapshots]);

  const messageForPreview = useMemo(() => {
    if (!messageWithEntitySnapshots) return null;
    if (!messageWithEntitySnapshots.reply_to_message_id) return messageWithEntitySnapshots;

    // Mirror the replyToMessage shape the feed's reply resolution provides for reply rendering.
    // `replyToMessage` is attached as `message.replyToMessage`, and `replyToStatus` indicates missing/loading.
    if (replyParentLoading) {
      return { ...messageWithEntitySnapshots, replyToMessage: null, replyToStatus: 'loading' };
    }

    if (replyToMessageWithEntitySnapshots) {
      return { ...messageWithEntitySnapshots, replyToMessage: replyToMessageWithEntitySnapshots, replyToStatus: null };
    }

    return { ...messageWithEntitySnapshots, replyToMessage: null, replyToStatus: 'failed' };
  }, [messageWithEntitySnapshots, replyToMessageWithEntitySnapshots, replyParentLoading]);

  const handleDelete = useCallback(async () => {
    if (!messageForPreview?.id || !messageForPreview?.channel_id) return;

    const response = await apiDelete(`/channels/${messageForPreview.channel_id}/messages/${messageForPreview.id}`);
    const data = await response.json();

    if (!data.ok) {
      logError('DeleteMessageConfirmation', 'Failed to delete message', { message: messageForPreview, errors: data.errors });
      alert('Could not delete message. Please try again.');
      return;
    }

    await syncWorkspaceLatestMessageChanges({ confirmedDeletes: [messageForPreview] });

    // Let parent clear edit state immediately after confirmed delete.
    selfDeletedRef.current = true;
    onDeleted?.(messageForPreview.id);
    onClose();
  }, [messageForPreview, onClose, onDeleted]);

  // track button refs for hotkey routing
  const cancelButtonRef = useRef(null);
  const deleteButtonRef = useRef(null);

  // handle enter key routing to the focused button
  const handleEnterRouting = useCallback(() => {
    if (!unlocked()) return;
    if (deleteButtonRef.current === document.activeElement) handleDelete();
    if (cancelButtonRef.current === document.activeElement) onClose();
  }, [unlocked, handleDelete, onClose]);

  const hotkeyRef = useHotkeys('enter', handleEnterRouting, { enableOnFormTags: true, preventDefault: true }, [handleEnterRouting]);

  // auto focus the delete button
  useEffect(() => deleteButtonRef.current?.focus(), []);

  // Defensive only — treat it as untested. isLoading requires both an unresolved feed row
  // and no retained row, and every dispatcher of the delete-modal event now carries the
  // renderer row, so this branch is not reachable today.
  if (isLoading) {
    return (
      <Modal onClose={onClose} ref={hotkeyRef}>
        <ModalBody>
          <ModalHeader>
            <ModalTitle>Delete message</ModalTitle>
          </ModalHeader>
        </ModalBody>
      </Modal>
    );
  }

  if (deletedByOther || !messageForPreview) {
    return (
      <Modal onClose={onClose} ref={hotkeyRef}>
        <ModalBody>
          <ModalHeader>
            <ModalTitle>Delete Message</ModalTitle>
          </ModalHeader>
          <div className="text-sm text-black dark:text-neutral-200">
            Could not find message. It may have already been deleted, or you may not have permission to delete it.
          </div>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} ref={hotkeyRef}>
      <ModalBody>
        <ModalHeader>
          <ModalTitle>Delete message</ModalTitle>
        </ModalHeader>

        <div className="max-h-100 overflow-x-hidden overflow-y-auto">
          <div className="rounded-glass bg-gray-200/60 p-5 dark:bg-neutral-800">
            <Message message={messageForPreview} author={author} interactive={false} standalone={true} />
          </div>
        </div>

        {/* Actions */}
        <div className="mt-5 flex justify-end gap-2">
          <SettingsButton ref={cancelButtonRef} onClick={onClose} variant="secondary">
            Cancel
          </SettingsButton>
          <SettingsButton ref={deleteButtonRef} onClick={handleDelete} variant="danger">
            Delete
          </SettingsButton>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default DeleteMessageConfirmation;
